/**
 * Who is cooking this meal — either assigned or awaiting assignment.
 *
 * Two variants because the live cards have incompatible space:
 *   'compact'  — desktop SlotCard and DesktopMealCard: just the avatar
 *                and short label. Claim pill appears only on hover.
 *   'inline'   — mobile MealRow: always visible. Claim pill always shown.
 *
 * The avatar is a switch, not a picker: tap cycles empty -> me -> the other
 * -> empty. Holding no opinion (unassigned) renders the claim pill; once
 * assigned, the avatar + name are shown until reset to unassigned.
 */
import React, { useCallback } from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';
import { setMealCook, selectMealById, getTodayStart } from '../../redux/mealPlanSlice';
import type { RootState } from '../../redux/store';
import { useHouseholdMembers } from '../../hooks/useHouseholdMembers';
import { getCookState, nextCookOnTap } from './cookState';
import { nameForViewer } from '../../services/householdService';

interface Props {
  mealId: string | undefined;
  variant: 'compact' | 'inline';
  isHovered?: boolean;
}

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export function CookRow({ mealId, variant, isHovered }: Props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { myUid, partnerUid, roster, householdId } = useHouseholdMembers();
  const meal = useSelector((s: RootState) => (mealId ? selectMealById(s, mealId) : null));

  const input = { meal, myUid, partnerUid, householdId, today: getTodayStart() };
  const state = getCookState(input);

  const handlePress = useCallback(
    (e: any) => {
      e?.stopPropagation?.();
      if (!mealId || !myUid) return;
      const nextUid = nextCookOnTap(meal?.cookId, myUid, partnerUid);
      dispatch(setMealCook({ mealId, uid: nextUid }));
    },
    [dispatch, mealId, myUid, meal?.cookId, partnerUid]
  );

  if (state === 'hidden') return null;

  const isCompact = variant === 'compact';
  const showClaimPill = state === 'unassigned' && (isCompact ? isHovered : true);
  const showAssignedCook = state === 'mine' || state === 'theirs';

  // Claim pill: visible on hover for compact, always for inline
  if (showClaimPill) {
    return (
      <Pressable
        onPress={handlePress}
        hitSlop={HIT}
        accessibilityRole="button"
        accessibilityLabel={t('household.approval.claim')}
        style={[styles.claimPill]}
      >
        <Text style={styles.claimText}>{t('household.approval.claim')}</Text>
      </Pressable>
    );
  }

  // Assigned cook: avatar + name
  if (showAssignedCook) {
    const cookId = meal?.cookId;
    if (!cookId) return null;

    const isMine = state === 'mine';
    const avatarColor = isMine ? Paprika.primary : Paprika.textSecondary;
    const displayName = nameForViewer(cookId, myUid, roster, t('household.approval.self'));
    const i18nKey = isMine ? 'household.approval.cooksYou' : 'household.approval.cooks';
    const label = isMine
      ? t(i18nKey)
      : t(i18nKey, { name: displayName });

    return (
      <Pressable
        onPress={handlePress}
        hitSlop={HIT}
        accessibilityRole="button"
        accessibilityLabel={label}
        style={[styles.assignedRow]}
      >
        <View style={[styles.avatar, { backgroundColor: avatarColor }]}>
          <Text style={styles.avatarInitial}>
            {displayName.charAt(0).toUpperCase()}
          </Text>
        </View>
        <Text style={styles.label}>{label}</Text>
      </Pressable>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  assignedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  avatar: {
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarInitial: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 8,
    color: Paprika.surfaceLowest,
  },
  label: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: Paprika.textMuted,
  },
  claimPill: {
    backgroundColor: Paprika.brandTint,
    borderWidth: 1,
    borderColor: Paprika.brandSoft,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  claimText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10.5,
    color: Paprika.primary,
  },
});
